import {
  WSActions,
  WSElement,
  WSList,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { WSQueries } from "../../../../query/WSQuery";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryPayer } from "../../../../query/payers/queries/useQueryPayer";
import { useQueryRequirementsList } from "../../../../query/requirements/queries/useQueryRequirementsList";
import { selectorIsAllRequirementsComplete } from "../../../Requirements/selectors/selectorIsAllRequirementsComplete";
import { CardAdditionalRequirementsRequester } from "../../components/CardAdditionalRequirementsRequester";
import { OnboardingStep } from "../../types";
import { PayerRequirements } from "./PayerRequirements";

const PayerTitle: React.FC<{ payerId: string }> = ({ payerId }) => {
  const queryPayer = useQueryPayer(payerId);
  const payer = queryPayer.data;

  if (!payer) {
    return (
      <WSElement shimmer>
        <WSSectionToolbar title="Requirements" mb="M" />
      </WSElement>
    );
  } else {
    const names = wsName({
      user: payer.user!,
      member: payer.member,
      payeeOwnedData: payer.payeeOwnedData
    });

    return (
      <WSSectionToolbar
        title={`${names.getResolvedName()} requirements`}
        mb="M"
      />
    );
  }
};

export const EligibilityRequirements: React.FC<OnboardingStep> = ({
  title,
  onNext,
  onSkip
}) => {
  const userId = useUserId();

  const queryRequirements = useQueryRequirementsList({
    filter: {
      payeeId: userId,
      payerPayeeEngagementIds: {
        "!=": "null"
      }
    },
    sort: {
      updatedAt: "desc"
    }
  });

  const isCompleted = queryRequirements.data
    ? selectorIsAllRequirementsComplete(queryRequirements.data)
    : false;

  const isSkipActive = queryRequirements.isFetched;

  return (
    <WSList gap="2XL">
      <CardAdditionalRequirementsRequester />

      <WSQueries queries={{ queryRequirements }}>
        {({ queryRequirementsData }) => {
          const requirementsGroupedByPayer = queryRequirementsData?.reduce(
            (acc, requirement) => {
              const payerId = requirement.payerId;
              if (!acc[payerId]) {
                acc[payerId] = [];
              }
              acc[payerId].push(requirement);
              return acc;
            },
            {} as Record<string, IRequirementResponse[]>
          );

          return isCompleted ? (
            <WSPanel p="XL">
              <WSSectionToolbar
                title={`All eligibility requirements is completed!`}
                mb="M"
              />
              <WSText.ParagraphSm mt="L" color="gray500">
                Press Continue to proceed to the next step.
              </WSText.ParagraphSm>
            </WSPanel>
          ) : (
            Object.entries(requirementsGroupedByPayer)
              .filter(([payerId, requirements]) => payerId !== userId)
              .map(([payerId, requirements]) => {
                return (
                  <WSElement key={payerId}>
                    <PayerTitle payerId={payerId} />
                    <PayerRequirements requirements={requirements} />
                  </WSElement>
                );
              })
          );
        }}
      </WSQueries>

      <WSActions
        alignment="fill"
        orientation="vertical"
        buttons={[
          {
            label: "Continue",
            kind: "Primary",
            disabled: !isCompleted,
            onClick: onNext
          },
          ...(isSkipActive && !isCompleted
            ? [
                {
                  label: "Skip for now",
                  kind: "Link",
                  onClick: onSkip
                } as const
              ]
            : [])
        ]}
      />
    </WSList>
  );
};
